import { Builder } from '@builder.io/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import sanitizeHtml from 'sanitize-html';
import { twMerge } from 'tailwind-merge';

import {
  loadBuilderDataOrFallback,
  setSearchBuilderData,
} from '../../../app/Search/actions/SearchActions';
import { selectSearchBuilderData } from '../../../app/Search/selectors/searchSelectors';
import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';

type RichTextData = {
  header: string;
  content: string;
};

const RichText = ({
  urlSlug,
  modelName = 'category-search-page',
  altHeader,
  altContent,
  header,
  content,
}: {
  urlSlug?: string;
  modelName?: string;
  altHeader?: string;
  altContent?: string;
  header?: string;
  content?: string;
}) => {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const builderContent = useSelector(selectSearchBuilderData);
  const [isLoading, setIsLoading] = useState(urlSlug ? true : false);

  useEffect(() => {
    setIsLoading(true);
    if (urlSlug) {
      // If urlSlug is provided, fetch the content
      dispatch(
        loadBuilderDataOrFallback(urlSlug, modelName, {
          richTextHeader: altHeader,
          richTextContent: altContent,
          faqItems: [],
          urlSlug,
        })
      ).finally(() => {
        setIsLoading(false);
      });
    } else {
      // If no urlSlug is provided, use the header and content directly
      if (header && content) {
        dispatch(
          setSearchBuilderData({
            richTextHeader: header,
            richTextContent: content,
            faqItems: [],
            urlSlug: '',
          })
        );
      }
      setIsLoading(false);
    }
  }, [urlSlug, modelName, header, content]);

  // Leaving a very strict sanitize config here to prevent XSS attacks and weird HTML
  const sanitizeConfig: sanitizeHtml.IOptions = {
    allowedTags: ['b', 'i', 'em', 'strong', 'a'],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
    },
    allowedSchemes: ['http', 'https', 'mailto'],
  };

  const sanitizedRichText: RichTextData = {
    header: sanitizeHtml(
      builderContent.richTextHeader || altHeader || header,
      sanitizeConfig
    ),
    content: sanitizeHtml(
      builderContent.richTextContent || altContent || content,
      sanitizeConfig
    ),
  };

  return (
    <div className="flex flex-col gap-2 items-center justify-center max-w-[483px] mx-auto">
      {sanitizedRichText.header && (
        <Text
          fontFamily="inter"
          fontWeight="semibold"
          fontSize="3xl"
          className={twMerge(
            'text-center',
            isLoading
              ? 'text-transparent bg-gray-300 animate-pulse rounded'
              : 'text-neutral-900'
          )}
          asChild
        >
          <h1 dangerouslySetInnerHTML={{ __html: sanitizedRichText.header }} />
        </Text>
      )}
      {sanitizedRichText.content && (
        <Text
          fontFamily="inter"
          fontSize="base"
          fontWeight="regular"
          className={twMerge(
            'text-center mt-0',
            isLoading
              ? 'text-transparent bg-gray-300 animate-pulse rounded'
              : 'text-neutral-900'
          )}
          asChild
        >
          <p dangerouslySetInnerHTML={{ __html: sanitizedRichText.content }} />
        </Text>
      )}
    </div>
  );
};

export default RichText;

Builder.registerComponent(RichText, {
  name: 'RichText SB',
  inputs: [],
});
